import { z } from 'zod';

/**
 * Roles a school invites by email; students get accounts another way (created by the school,
 * activated by the guardian in M2).
 */
export const invitableRoleSchema = z.enum(['SCHOOL_OWNER', 'SCHOOL_STAFF', 'TEACHER', 'GUARDIAN']);

/**
 * Body of `POST /invitations/bulk`: everyone of a role without an account, or a given list.
 */
export const bulkInviteInputSchema = z
  .object({
    role: z.enum(['TEACHER', 'GUARDIAN']).optional(),
    userIds: z.array(z.string().uuid()).max(5000).optional(),
  })
  .refine((input) => input.role !== undefined || input.userIds !== undefined, 'role or userIds');

/**
 * Query of `GET /invitations`.
 */
export const invitationsQuerySchema = z.object({
  userId: z.string().uuid().optional(),
  status: z.enum(['pending', 'accepted', 'expired', 'revoked']).optional(),
});

/**
 * Body of `POST /public/invitations/{token}/accept`: the password the invitee chooses.
 */
export const acceptInvitationInputSchema = z.object({
  password: z.string().min(8).max(256),
});

/**
 * An invitable role.
 */
export type InvitableRole = z.infer<typeof invitableRoleSchema>;
/**
 * Body of `POST /invitations/bulk`.
 */
export type BulkInviteInput = z.input<typeof bulkInviteInputSchema>;
/**
 * Query of `GET /invitations`.
 */
export type InvitationsQuery = z.infer<typeof invitationsQuerySchema>;
/**
 * Body of the accept endpoint.
 */
export type AcceptInvitationInput = z.input<typeof acceptInvitationInputSchema>;

/**
 * Lifecycle of an invitation, derived from its dates.
 */
export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

/**
 * An invitation as the school sees it (never the token).
 */
export type InvitationView = {
  id: string;
  userId: string;
  email: string;
  role: InvitableRole;
  status: InvitationStatus;
  expiresAt: string;
  acceptedAt: string | null;
  resendCount: number;
  lastSentAt: string | null;
  lastEmailStatus: string | null;
  createdAt: string;
};

/**
 * What the invitee sees before choosing a password: who invites them, as what, and what they
 * will find inside (their children for a guardian, their groups for a teacher).
 */
export type PublicInvitationView = {
  status: InvitationStatus;
  schoolName: string;
  inviteeName: string;
  email: string;
  role: InvitableRole;
  expiresAt: string;
  students: { name: string; group: string | null }[];
  assignments: { group: string; subject: string | null }[];
};

/**
 * What accepting answers: the email to sign in with and the school.
 */
export type AcceptInvitationResult = { email: string; tenantId: string; schoolName: string };

/**
 * A guardian's child as `GET /me/students` lists them.
 */
export type FamilyStudent = {
  id: string;
  givenName: string | null;
  familyName: string | null;
  relationship: string;
  isPrimary: boolean;
  billingResponsible: boolean;
  enrollment: { schoolYear: string; group: string; grade: string; sede: string } | null;
};

/**
 * What `POST /students/{id}/account` answers.
 */
export type StudentAccountResult = { id: string; email: string; status: string };
